import {
    Button,
    Card,
    Col,
    Collapse,
    Divider,
    DownOutlined,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
} from 'gokwik-ui-kit'
import { basicDetailsFormConfig } from './config'
import { useEffect, useState } from 'react'
import { CollapsePanelStatusChips, getFormStatusConfigs, handleFieldChange } from '@pages/onboarding/kyc/utils'
import { getUserData } from '@store/user/selectors'
import { useSelector } from 'react-redux'
import { stepConfiguration } from '@pages/onboarding/stepsConfig'
import { logEvent } from '@library/utilities/userLogsEvents/userLogEvents'
import { getPrimaryWebsiteUrl } from '@library/utilities/helpers/helper'
import { makeAPICall } from '@gokwik/utilities'
import APIEndPoints from '@library/utilities/constants/apiEndpoints'
import { fetchUserDetails } from '@store/user'
import { useAppDispatch } from '@library/utilities/hooks'

const BasicDetails = ({
    kycData,
    onFinish,
    setCurrentStep,
    currentStep,
    isPanelClickable,
    undeterminedKycErrorState,
    form,
    hasGenericError,
}) => {
    const userData = useSelector(getUserData)
    const [basic_details_fields, set_basic_details_fields] = useState(basicDetailsFormConfig?.items)
    const [errors, setError] = useState([])
    const [dynamicErrors, setDynamicErrors] = useState({})
    const current_onboarding_status = userData?.merchantDetails?.onboarding_status?.kwik_checkout_app
    const [primaryWebsite, setPrimaryWebsite] = useState('')
    const [isModalOpen, setIsModalOpen] = useState(false)
    const currentEntity = form.getFieldsValue('entity_type') ?? ''
    const [currentEntityType, setCurrentEntityType] = useState(currentEntity)
    const dispatch = useAppDispatch()

    const showModal = () => {
        setIsModalOpen(true)
    }

    const updateMerchantOnboardingStatusToInitialStage = async () => {
        await makeAPICall({
            method: 'post',
            url:
                process.env.REACT_APP_BASE_URL +
                APIEndPoints.updateMerchantOnboardingStatus +
                userData?.merchantDetails?.m_id,
            payload: {
                current_onboarding_status: 'kyc_viewed',
            },
        })

        dispatch(fetchUserDetails())
    }

    const handleOk = () => {
        sessionStorage.setItem('entity_type', currentEntityType)
        updateMerchantOnboardingStatusToInitialStage()
        setIsModalOpen(false)
    }

    const handleCancel = () => {
        setIsModalOpen(false)
    }

    const setPrimaryWebsiteUrl = async () => {
        const primaryWebsiteUrl = await getPrimaryWebsiteUrl(kycData?.website)

        if (primaryWebsiteUrl?.length > 0) {
            setPrimaryWebsite(primaryWebsiteUrl)
            return primaryWebsiteUrl
        }

        return ''
    }

    useEffect(() => {
        const updateFormFields = async () => {
            const updatedFormItems = await Promise.all(
                basic_details_fields.map(async (item) => {
                    if (item.name === 'business_name') {
                        if (
                            ['mystore', 'my store', 'my_store'].includes(kycData?.business_name?.trim().toLowerCase())
                        ) {
                            return {
                                ...item,
                                validateStatus: 'error',
                                help: 'This looks like a default name. Enter your actual brand name for accuracy.',
                            }
                        }
                    }

                    if (item.name === 'website') {
                        const url = await setPrimaryWebsiteUrl()
                        if (!url) {
                            return {
                                ...item,
                                validateStatus: 'error',
                                help: 'Invalid URL, Please add valid url to proceed',
                            }
                        }
                    }

                    return item
                }),
            )

            const getStatus = getFormStatusConfigs(userData, updatedFormItems, 'basic_info')

            setError(getStatus?.generateErrorChips)
            const configFields = getStatus?.updateConfigWithErrors()
            set_basic_details_fields(configFields)
        }

        updateFormFields()
    }, [userData, kycData])

    const isFieldEnabled = (fieldName) => {
        if (current_onboarding_status === 'kyc_verification_failed') {
            if (
                Object.keys(userData?.kycData?.kyc_failure_reasons)?.includes(fieldName) ||
                undeterminedKycErrorState ||
                hasGenericError(userData?.kycData?.kyc_failure_reasons, ['basic_address_generic_error'])
            ) {
                return false //dont-disable-the-field
            } else {
                return true //yes-disable-the-field
            }
        } else if (
            current_onboarding_status === 'kyc_verification_pending' ||
            current_onboarding_status === 'kyc_completed'
        ) {
            return true //yes-disable-the-field
        }
    }

    const handleChange = (value) => {
        if (value) {
            form.setFieldsValue({ entity_type: userData?.kycData?.entity_type })
            setCurrentEntityType(value)
            setIsModalOpen(true)
            logEvent(
                'merchant_edits_prefilled_data',
                'click',
                'kyc_bank_details',
                userData?.userDetails?.email,
                userData?.merchantDetails?.m_id,
                userData?.merchantDetails?.short_name,
                userData?.userDetails?.name,
                { changed_entity_type: value },
            )
        }
    }

    const items = [
        {
            key: '1',
            label: (
                <>
                    <div className='flex flex-col gap-1'>
                        <div className='text-[#004B8D] text-xs font-semibold'>Step 1</div>

                        <div className='flex gap-4 items-center'>
                            <div className={`text-xl font-semibold ${errors?.length && 'text-red-700'}`}>
                                Basic Information
                            </div>{' '}
                            <CollapsePanelStatusChips
                                errors={errors}
                                stepConfiguration={stepConfiguration}
                                current_onboarding_status={
                                    userData?.merchantDetails?.onboarding_status?.kwik_checkout_app
                                }
                                sectionName={'basic_info'}
                                dynamicErrors={dynamicErrors}
                            />
                        </div>
                    </div>
                </>
            ),
            children: (
                <Form
                    initialValues={kycData || {}}
                    form={form}
                    scrollToFirstError={{ behavior: 'auto', block: 'center' }}
                    onFinish={(v) => {
                        logEvent(
                            'basic_info_save_continue_clicked',
                            'click',
                            'kyc',
                            userData?.userDetails?.email,
                            userData?.merchantDetails?.m_id,
                        )
                        onFinish(v, 0)
                    }}
                    layout='vertical'
                    onFieldsChange={(changedField, allFields) =>
                        handleFieldChange(changedField, basic_details_fields, set_basic_details_fields)
                    }
                >
                    <div className={`text-sm font-semibold ${errors?.length && 'text-red-700'}`}>Basic Information</div>
                    <Row gutter={16} className='!text-sm'>
                        {basic_details_fields?.map((item, index) => (
                            <Col span={item?.span || 12} key={index}>
                                <Form.Item
                                    className='m-3 ml-0'
                                    extra={
                                        item.name === 'entity_type' && !isFieldEnabled(item?.name) ? (
                                            <span className='text-orange-400'>{item?.warning}</span>
                                        ) : null
                                    }
                                    label={<span className='text-sm font-normal text-[#000000E0]'>{item.label}</span>}
                                    name={item.name}
                                    rules={item.rules}
                                    {...((item as any)?.validateStatus &&
                                        (item?.name === 'business_name' ||
                                            //@ts-ignore
                                            (item?.name === 'website' && item?.help?.includes('URL'))) && {
                                            validateStatus: (item as any)?.validateStatus,
                                        })}
                                    {...((item as any)?.help &&
                                        (item?.name === 'business_name' ||
                                            //@ts-ignore
                                            (item?.name === 'website' && item?.help?.includes('URL'))) && {
                                            help: (item as any)?.help,
                                        })}
                                >
                                    {item.type === 'input' && (
                                        <Input
                                            placeholder={item.placeholder}
                                            disabled={item.disabled || isFieldEnabled(item?.name)}
                                        />
                                    )}
                                    {item.type === 'inputNumber' && (
                                        <InputNumber
                                            placeholder={item.placeholder}
                                            className='w-full'
                                            disabled={isFieldEnabled(item?.name)}
                                        />
                                    )}
                                    {item.type === 'select' && (
                                        <Select
                                            {...(item.name === 'entity_type' && { onChange: handleChange })}
                                            filterSort={null}
                                            placeholder={item.placeholder}
                                            disabled={isFieldEnabled(item?.name)}
                                        >
                                            {item.name === 'business_category'
                                                ? userData?.kycData?.allowed_business_categories.map((option) => (
                                                      <Select.Option key={option} value={option}>
                                                          {option}
                                                      </Select.Option>
                                                  ))
                                                : []}
                                            {item.name === 'entity_type'
                                                ? item.options.map((option, index) => (
                                                      <Select.Option key={index} value={Number(Object.values(option))}>
                                                          {Object.keys(option)[0]}
                                                      </Select.Option>
                                                  ))
                                                : []}
                                        </Select>
                                    )}
                                </Form.Item>
                            </Col>
                        ))}
                    </Row>

                    {/* <Divider className='my-4' />
                    <Form.Item className='flex justify-end mb-0'>
                        <Button
                            variant='primary'
                            htmlType='submit'
                            disabled={
                                (errors?.length === 0 &&
                                    current_onboarding_status === 'kyc_verification_failed' &&
                                    !userData?.kycData?.kyc_rejection_reason) ||
                                userData?.merchantDetails?.onboarding_status?.kwik_checkout_app ===
                                    'kyc_verification_pending' ||
                                current_onboarding_status === 'kyc_completed'
                            }
                        >
                            Save & Continue
                        </Button>
                    </Form.Item> */}
                </Form>
            ),
        },
    ]

    const onChange = (e) => {
        setCurrentStep(0)
    }

    return (
        <div className=''>
            <Modal title='Do you want to proceed ?' open={isModalOpen} onOk={handleOk} onCancel={handleCancel}>
                Changing/Updating Registration type now will require a fresh re-submit of KYC documents altogether.
            </Modal>
            <Card>{items[0]?.children}</Card>
        </div>
    )
}

export default BasicDetails
